import React, { Fragment, useState, useRef, useEffect } from 'react'

// import Popup from "reactjs-popup";
import { apiSubscribeUpgrade } from 'src/utils/journeyApi';
import { useCurrentOrganization } from 'src/store/organization';
import { SubscriptionPaymentIntervalTypes } from 'src/dashboard/settings/billing';

import { Dialog, Transition } from '@headlessui/react'
import { XIcon,  CheckIcon, ArrowRightIcon } from '@heroicons/react/outline'

import classnames from "classnames";

import { NavLink, useHistory } from 'react-router-dom';
import { useCurrentSubscription } from 'src/store/subscription';
import { DashboardSettingsBillingFeatureMapping } from 'src/dashboard/settings/billing/feature-mapping';
import { getFeatureRequiredPlan } from 'src/utils/feature-restrictions';
import { useNotificationMessage } from 'src/common/notification/message.hook';
import { UpgradeConfirmationModal } from 'src/dashboard/settings/billing/subscription/upgrade-confirmation-modal';

const appendBillingMode : { MONTHLY: SubscriptionPaymentIntervalTypes; YEARLY: SubscriptionPaymentIntervalTypes } ={
    MONTHLY: 'month',
    YEARLY: 'year'
}

const absolutePriceConstants = {
    JOURNEY_PRO_MONTHLY: 29,
    JOURNEY_PRO_PAID_YEARLY: 24.08,
    JOURNEY_PRO_ABSOLUTE_YEARLY: 289,
    JOURNEY_ENTERPRISE_MONTHLY: 59,
    JOURNEY_ENTERPRISE_PAID_YEARLY: 49.08,
    JOURNEY_ENTERPRISE_ABSOLUTE_YEARLY: 589
}

interface payloadMsg{
    msg?: string
    cancelModal: () => void
    feature?: string
}

interface ModalPayload {
    msg?: string
    feature?: string
}

const FormContents = ({msg, cancelModal, feature}: payloadMsg) => {

    const { currentOrganization } = useCurrentOrganization();
    const { setErrorNotification, setSuccessNotification } = useNotificationMessage();

    const {customMsg, customHeader} = useCurrentSubscription()

    const [activeProInterval, setActiveProInterval] = useState<SubscriptionPaymentIntervalTypes>('year');
    const [showUpgradeModal, setShowUpgradeModal] = useState(false);
    const [isUpgrading, setIsUpgrading] = useState(false);

    const organizationSlug = currentOrganization?.slug;

    // Determine which plan to show based on the feature or custom message
    const getRequiredPlanForModal = () => {
        // If a specific feature is provided, use the feature restrictions system
        if (feature) {
            const requiredPlan = getFeatureRequiredPlan(feature);
            return requiredPlan || 'pro'; // Default to Grow plan if no specific plan required
        }
        
        // Fallback to message parsing for backward compatibility
        const message = customMsg || '';
        const header = customHeader || '';
        
        // Check for features that require Grow or Scale plan
        const growFeatures = [
            'workspaces',
            'tags and filters',
            'ai chatbot',
            'email tracking',
            'made in journey branding'
        ];
        
        // Check for specific features that require Scale plan
        const scaleFeatures = [
            'organization templates',
            'customize nda',
            'clay integration',
            'slack notifications',
            'session replay',
            'advanced insights'
        ];
        
        const messageLower = message.toLowerCase();
        // Prioritize grow detection first
        if (growFeatures.some(growFeature => messageLower.includes(growFeature))) {
            return 'pro';
        }
        
        // Detect scale features or explicit scale mention
        if (scaleFeatures.some(scaleFeature => messageLower.includes(scaleFeature)) ||
            header.toLowerCase().includes('scale') || header.toLowerCase().includes('enterprise')) {
            return 'enterprise';
        }
        
        // Default to Grow plan
        return 'pro';
    };

    const requiredPlan = getRequiredPlanForModal();
    const isScalePlan = requiredPlan === 'enterprise';

    // Get plan details based on required plan
    const getPlanDetails = () => {
        if (isScalePlan) {
            return {
                name: 'Journey Scale',
                monthlyPrice: absolutePriceConstants.JOURNEY_ENTERPRISE_MONTHLY,
                yearlyPrice: absolutePriceConstants.JOURNEY_ENTERPRISE_PAID_YEARLY,
                absoluteYearly: absolutePriceConstants.JOURNEY_ENTERPRISE_ABSOLUTE_YEARLY,
                features: DashboardSettingsBillingFeatureMapping.ScalePlan
            };
        } else {
            return {
                name: 'Journey Grow',
                monthlyPrice: absolutePriceConstants.JOURNEY_PRO_MONTHLY,
                yearlyPrice: absolutePriceConstants.JOURNEY_PRO_PAID_YEARLY,
                absoluteYearly: absolutePriceConstants.JOURNEY_PRO_ABSOLUTE_YEARLY,
                features: DashboardSettingsBillingFeatureMapping.GrowPlan
            };
        }
    };

    const planDetails = getPlanDetails();

    const handleUpgradeConfirm = async () => {
        setIsUpgrading(true);
        try {
            await apiSubscribeUpgrade(currentOrganization.id, {
                method: activeProInterval,
                plan: requiredPlan
            });
            
            setSuccessNotification('Subscription upgraded successfully!');
            setShowUpgradeModal(false);
            cancelModal();
            
            // Reload the page to reflect the changes
            window.location.reload();
        } catch (error) {
            console.error('Failed to upgrade subscription:', error);
            setErrorNotification('Failed to upgrade subscription. Please try again.');
        } finally {
            setIsUpgrading(false);
        }
    };

    const checkoutProduct = async (intervalType: SubscriptionPaymentIntervalTypes) => {
        // Show confirmation modal for upgrade
        setShowUpgradeModal(true);
    };

    const onSubcribeIntervalChange = (intervalType: SubscriptionPaymentIntervalTypes) => {
        if (activeProInterval === intervalType) {
            return;
        }
        setActiveProInterval(intervalType);
    };

    return (
        <>
        {
            (customMsg && customMsg!=='')? 
        <div className='px-4 sm:px-6 text-left text-sm' dangerouslySetInnerHTML={{__html: customMsg}}/>
        :
        <div className='px-4 sm:px-6 text-left text-sm'>Comprehensive Analytics helps you understand your prospects. Anonymously tracks the country, browser and devices of viewers interacting with your journey<br/><br/> To unlock comprehensive analytics, upgrade to the Pro Plan</div>

        }

        <div className="flex items-center justify-center mt-5 space-x-4">
            
                    <span className="text-sm font-medium">Bill Monthly</span>
                    <button
                        className="relative rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        onClick={() =>
                        activeProInterval === appendBillingMode.MONTHLY
                            ? onSubcribeIntervalChange(appendBillingMode.YEARLY)
                            : onSubcribeIntervalChange(appendBillingMode.MONTHLY)
                        }
                    >
                        <div
                            className={classnames(
                                "w-12 h-6 transition rounded-full shadow-md outline-none",
                                activeProInterval === appendBillingMode.MONTHLY ? "bg-gray-300" : "bg-blue-500"
                            )}
                        />
                        <div
                        style={{top:'2px'}}
                            className={classnames(
                                "absolute inline-flex items-center justify-center w-5 h-5 transition-all duration-200 ease-in-out transform bg-white rounded-full shadow-sm left-1",
                                activeProInterval === appendBillingMode.MONTHLY ? "translate-x-0" : "translate-x-6"
                            )}
                        />
                    </button>
                    <span className="text-sm font-medium">
                        Bill Annually (17% off)
                    </span>
                </div>
               

        <div className="flex-1 flex flex-col justify-between">
                            <div className="px-4  sm:px-6">
                                <div className="pt-5 px-12">

        <div className='border-gray-700 border-2 px-6 py-6 rounded-md'>
                        <div className='text-xl font-bold'>{planDetails.name}</div>

                        {
               
                        <div className=" mt-2 block mx-auto">
                        <div className="flex-shrink-0">
                            <span className="text-xl font-medium tracking-tight">
                            {activeProInterval === appendBillingMode.MONTHLY
                                ? `$${planDetails.monthlyPrice}`
                                : `$${planDetails.yearlyPrice}`}
                            </span>
                            <span className="text-gray-800">
                            {" /month"}
                            </span>

                        </div>
                        {activeProInterval === appendBillingMode.YEARLY &&
                            <div className="mt-1">
                            <span className="text-sm tracking-tight text-gray-800 font-roboto font-medium">
                                {` paid $${planDetails.absoluteYearly} yearly`}
                            </span>
                            </div>

                        }
                        </div>
        }
                       
                        <ul className="inline-flex flex-col mt-4 justify-center mx-auto space-y-1">
                            {planDetails.features.map((feature, idx) => (
                            <li key={idx} className="flex items-start">
                                <CheckIcon className="w-6 h-6" style={{minWidth: '1.5rem', minHeight: '1.5rem', maxWidth: '1.5rem', maxHeight: '1.5rem'}}/>
                                <span className={'ml-2 text-base text-left'}>{feature}</span>
                            </li>
                            ))}
                        </ul>

            
                <div
                onClick={() => checkoutProduct(activeProInterval)}
                className="relative block items-center max-w-max mt-4 mx-auto space-x-1 cursor-pointer text-white bg-gray-700 hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 py-2 px-4 border border-transparent text-base font-semibold rounded-md transition duration-150 ease-in-out">
                <span className="inline-flex">Upgrade</span>
            </div>
           
                </div>
                       

                        
                          
                        </div>
                    
                        </div>
                    </div>
                  
                <NavLink to={`/${organizationSlug}/billing`} onClick={()=>cancelModal()} className="flex-shrink-0 pt-4 pb-6 flex justify-center text-blue-700">
                <span className='mr-2'>View all plans</span>
                <span className='mt-1'><ArrowRightIcon className='w-4 h-4'/></span>
                </NavLink>

        <UpgradeConfirmationModal
            isOpen={showUpgradeModal}
            onClose={() => setShowUpgradeModal(false)}
            onConfirm={handleUpgradeConfirm}
            planName={planDetails.name}
            isLoading={isUpgrading}
        />
        </>
    )
}


const UpgradePricingModal = ({msg}: ModalPayload) => {
    const cancelButtonRef = useRef(null)

    const {openPricingPaywall, setOpenPricingPaywall, setCustomMsg, customHeader, setCustomHeader, feature, setFeature} = useCurrentSubscription()

    useEffect(()=>{},[openPricingPaywall])

    const cancelModal = () =>{
      setOpenPricingPaywall(false);
      setCustomMsg(null)
      setCustomHeader(null)
      setFeature(null)
    }

    return (
        <Transition.Root show={openPricingPaywall} as={Fragment}>
        <Dialog as="div" className="fixed inset-0 overflow-y-auto" style={{zIndex:99}} initialFocus={cancelButtonRef} onClose={()=>{}}>
          <div className="flex items-start mt-1 justify-center min-h-screen  px-4 pb-20 text-center ">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo= "opacity-0"
            >
              <Dialog.Overlay className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
            </Transition.Child>
  
            {/* This element is to trick the browser into centering the modal contents. */}
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">
              &#8203;
            </span>
  
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <div className="inline-block align-bottom bg-white rounded-lg px-2 sm:px-4 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:align-middle sm:max-w-xl sm:w-full sm:p-6">
                <div className="pb-6 px-4 sm:px-6 rounded-lg">
                  <div className="flex items-center justify-between">
                    <span className="text-lg font-medium text-gray-700">{customHeader || 'Upgrade Required'}</span>
                    <div className="ml-3 h-7 flex items-center">
                      <div
                        className="cursor-pointer rounded-md text-gray-700"
                        onClick={() => setOpenPricingPaywall(false)}
                        ref={cancelButtonRef}
                      >
                        <span className="sr-only">Close panel</span>
                        <XIcon className="h-6 w-6" aria-hidden="true" 
                          style={{ outline: "none", border: 'none', textDecoration: 'none' }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
  
                <div className="text-center">
                  <FormContents msg={msg} cancelModal={cancelModal} feature={feature || undefined} />
                </div>
              </div>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition.Root>
    )
}

export default UpgradePricingModal;